/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, createRef, RefObject } from 'preact';
import { useEffect, useRef, useState, StateUpdater, useCallback, Dispatch } from 'preact/hooks';
import { Flex } from '../UNSAFE_Flex';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { useSingleSelection } from '../hooks/PRIVATE_useSingleSelection/useSingleSelection';
import { useCurrentKey } from '../hooks/PRIVATE_useCurrentKey';
import { useCollectionFocusRing } from '../hooks/PRIVATE_useCollectionFocusRing';
import { keyExtractor, getPrevNextKey, isMobile } from '../utils/PRIVATE_collectionUtils';
import { useId } from '../hooks/UNSAFE_useId';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '../resources/nls/bundle';
import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';
import { ItemContext, CurrentKeyDetail, SingleSelectionDetail } from '../UNSAFE_Collection';
import { Collection } from '../PRIVATE_Collection';
import {
  Section,
  SectionItem,
  LastSectionItem,
  SeparatorItem,
  SECTION_OTHERS,
  hasSeparator,
  ITEM_SELECTOR
} from './IndexerItems';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { IndexerStyles } from './themes/IndexerStyles.css';
import { IndexerRedwoodTheme } from './themes/redwood/IndexerTheme';
import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';

export type CommitDetail<K> = {
  value: K;
};

type Props<K> = {
  /**
   * An array of sections, the default sections is an
   * array of 26 English alphabets and the # others character
   */
  sections?: Section<K>[];

  /**
   * The currently selected Section
   */
  value?: K;

  /**
   * A function that will be invoked when selection happens,
   * no matter the Section was selected before or not.
   */
  onCommit?: (detail: CommitDetail<K>) => void;
};

const defaultSections: Section<string>[] = [
  { key: 'A' },
  { key: 'B' },
  { key: 'C' },
  { key: 'D' },
  { key: 'E' },
  { key: 'F' },
  { key: 'G' },
  { key: 'H' },
  { key: 'I' },
  { key: 'J' },
  { key: 'K' },
  { key: 'L' },
  { key: 'M' },
  { key: 'N' },
  { key: 'O' },
  { key: 'P' },
  { key: 'Q' },
  { key: 'R' },
  { key: 'S' },
  { key: 'T' },
  { key: 'U' },
  { key: 'V' },
  { key: 'W' },
  { key: 'X' },
  { key: 'Y' },
  { key: 'Z' }
];

/**
 * Pure Preact based component that consumes Collection component
 * and renders an indexer.
 */
export function Indexer<K extends string | number>({
  sections = defaultSections as Section<K>[],
  value,
  onCommit = () => {},
  testId
}: Props<K> & TestIdProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const ulRef = useRef<HTMLUListElement>(null);
  const currElementRef = createRef<HTMLLIElement>();
  const [current, setCurrent] = useState<K | undefined>(sections[0]?.key);
  // the number of sections in each truncation
  const [sectionsPerTruncation, setSectionsPerTruncation] = useState<number>(-1);
  // the total number of rendered sections and separators
  const renderedSectionsCount = useRef<number>(-1);
  const itemHeightRef = useRef<number>(0);
  // the id for aria describedby
  const uniqueId = useId();

  const [showFocusRing, focusRingProps] = useCollectionFocusRing(rootRef, ['ArrowUp', 'ArrowDown']);

  const { currentKeyProps } = useCurrentKey(
    (element) => keyExtractor(element, ITEM_SELECTOR),
    true,
    getPrevNextKey(rootRef.current, current, true, ITEM_SELECTOR),
    getPrevNextKey(rootRef.current, current, false, ITEM_SELECTOR),
    undefined,
    undefined,
    current,
    (detail: CurrentKeyDetail<K>) => setCurrent(detail.value)
  );

  const { selectionProps } = useSingleSelection(
    (element) => handleKeyExtract(element, ulRef, current as K),
    // pass in undefined so onCommit handler will be called
    // no matter whether the Section was selected before or not
    undefined,
    (detail: SingleSelectionDetail<K>) => handleSelection(detail, onCommit, sections)
  );

  /**
   * Item renderer that will return a section item or a section separator
   * based on the sectionsPerTruncation and item context index.
   * @param context item context
   */
  const itemRenderer = (context: ItemContext<Section<K>>): ComponentChildren => {
    const last = sections.length - 1;
    renderedSectionsCount.current += 1;

    const commonProps = {
      context,
      ref: currElementRef,
      currentKey: current as K,
      selectedKey: value as K,
      valueNow: renderedSectionsCount.current,
      isFocusRingVisible: !!showFocusRing
    };

    // render the last and the others character, they are always present
    if (context.index === last) {
      return <LastSectionItem {...commonProps} lastValueNow={renderedSectionsCount.current + 1} />;
    }

    // render a section, in the non-truncation case or when it is the first in a truncation
    if (sectionsPerTruncation <= 1 || context.index % sectionsPerTruncation === 0) {
      return <SectionItem {...commonProps} />;
    }

    // render the separator in the middle of a truncation
    const separatorProps = { ...commonProps, sectionsPerTruncation, last, sections };
    if (hasSeparator(separatorProps)) {
      return <SeparatorItem {...separatorProps} />;
    }

    // remove the count for truncated sections
    renderedSectionsCount.current -= 1;

    return null;
  };

  // truncate the Indexer during resize
  const handleResize = useCallback(
    (entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        updateSectionsPerTruncation(
          entry.contentRect.height,
          itemHeightRef.current,
          sections.length,
          sections[0]?.key,
          sectionsPerTruncation,
          setSectionsPerTruncation,
          setCurrent
        );
      }
    },
    [sections, sectionsPerTruncation]
  );

  useEffect(() => {
    if (ulRef.current) {
      // Get aria values from IndexerItem to update aria values on Indexer ul element
      ulRef.current.setAttribute('aria-valuemax', renderedSectionsCount.current + 1 + '');
      ulRef.current.setAttribute(
        'aria-valuetext',
        currElementRef.current?.dataset?.['ojValuetext'] || ''
      );
      ulRef.current.setAttribute(
        'aria-valuenow',
        currElementRef.current?.dataset?.['ojValuenow'] || ''
      );
    }
    renderedSectionsCount.current = -1;
  });

  /**
   * Set up the initial values for sectionsPerTruncation and event listeners
   */
  useEffect(() => {
    const root = rootRef.current;
    if (root) {
      itemHeightRef.current = root.querySelector('li')?.offsetHeight || 0;
      updateSectionsPerTruncation(
        root.offsetHeight,
        itemHeightRef.current,
        sections.length,
        sections[0]?.key,
        sectionsPerTruncation,
        setSectionsPerTruncation,
        setCurrent
      );
    }
  }, [sections, sectionsPerTruncation]);

  /**
   * Set up resize observer and refresh the resize observer when
   * sectionsPerTruncation is changed, otherwise it will only have the
   * old copy of sectionsPerTruncation.
   */
  useEffect(() => {
    const root = rootRef.current;
    if (root) {
      const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        handleResize(entries);
      });
      resizeObserver.observe(root);
      return () => {
        resizeObserver.unobserve(root);
      };
    }
    return undefined;
  }, [sectionsPerTruncation, handleResize]);

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const {
    styles: { baseStyle, innerStyle }
  } = useComponentTheme<never, IndexerStyles>(IndexerRedwoodTheme);
  const testIdProps = useTestId(testId);

  return (
    <div ref={rootRef} class={baseStyle} {...testIdProps}>
      <Flex align="center" width="6x" height="100%">
        <ul
          {...mergeProps(currentKeyProps, focusRingProps, selectionProps)}
          ref={ulRef}
          tabIndex={0}
          aria-label="Indexer"
          aria-orientation="vertical"
          aria-valuemin={0}
          aria-describedby={uniqueId}
          role="slider"
          class={innerStyle}>
          <Collection items={sections}>{itemRenderer}</Collection>
        </ul>
      </Flex>
      <HiddenAccessible>
        <span id={uniqueId}>{getIndexerDescription(translations)}</span>
      </HiddenAccessible>
    </div>
  );
}

// A function to extract key for selection
const handleKeyExtract = <K extends string | number>(
  element: HTMLElement,
  rootRef: RefObject<HTMLElement>,
  current: K
) => {
  if (element === rootRef.current) {
    return current === undefined ? null : current;
  }
  return keyExtractor(element, ITEM_SELECTOR) as K;
};

const handleSelection = <K extends string | number>(
  detail: SingleSelectionDetail<K>,
  onCommit: (value: CommitDetail<K>) => void,
  sections: Section<K>[]
) => {
  if (detail.value === SECTION_OTHERS.key) {
    onCommit({ value: SECTION_OTHERS.key as K });
  } else {
    const selectedSection = sections.find((section) => section.key === detail.value) as Section<K>;
    onCommit({ value: selectedSection.key as K });
  }
};

/**
 * A function that calculates the section count, based on
 * the container height and the number of sections
 * @param height height of Indexer container
 */
const updateSectionsPerTruncation = <K extends string | number>(
  height: number,
  itemHeight: number,
  sectionsLength: number,
  firstSectionKey: K,
  sectionsPerTruncation: number,
  setSectionsPerTruncation: Dispatch<StateUpdater<number>>,
  setCurrent: Dispatch<StateUpdater<K | undefined>>
) => {
  if (height === 0 || itemHeight === 0) {
    return;
  }

  const maxSections = height / itemHeight;
  const last = sectionsLength - 1;

  // when the container size is too small to hold two sections
  if (maxSections < 2) {
    if (sectionsPerTruncation !== last) {
      setSectionsPerTruncation(last);
      setCurrent(firstSectionKey);
    }

    // no truncation
    // compare with < 0.01 for the deviation from calculation, when sectionsLength and maxSections
    // are about the same, maxNum === sectionsLength doesn't work
  } else if (maxSections > sectionsLength || sectionsLength - maxSections < 0.01) {
    if (sectionsPerTruncation > 1) {
      setSectionsPerTruncation(1);
      setCurrent(firstSectionKey);
    }

    // need truncation
  } else {
    const tempSectionCount = findSectionCount(maxSections, last);
    if (tempSectionCount !== sectionsPerTruncation && tempSectionCount < sectionsLength) {
      setSectionsPerTruncation(tempSectionCount);
      setCurrent(firstSectionKey);
    }
  }
};

/**
 * A helper function to find the best section count
 * in a truncation
 *
 * @param maxSections maximum number of sections allowed in a truncation
 * @param last the index of last section
 */
const findSectionCount = (maxSections: number, last: number) => {
  // need truncation
  maxSections -= 2; // minus the last character and the others character
  maxSections /= 2; // each truncation has a section and a separator

  const newSectionCount = Math.round(last / maxSections) + 1;
  const truncationLeftover = last % newSectionCount;
  const truncationCount = Math.floor(last / newSectionCount) + (truncationLeftover > 1 ? 1 : 0);

  // When the sectionCount becomes greater, it will result in
  // same truncations but with different sections appearing.
  // The following is to find the best sectionCount in this case
  // and prevent unnecessary truncations.
  let tempLeftover = last - (truncationCount - 1) * newSectionCount;
  let tempSectionCount = newSectionCount;
  while (tempSectionCount > tempLeftover) {
    tempSectionCount -= 1;
    tempLeftover = last - (truncationCount - 1) * tempSectionCount;
  }
  if (tempSectionCount != newSectionCount) {
    tempSectionCount += 1;
  }

  return tempSectionCount;
};

/**
 * Helper function to find the aria description for Indexer,
 * based on whether current device is a mobile device
 */
function getIndexerDescription(translations: any) {
  if (isMobile()) {
    return translations.indexer_touchInstructionText();
  } else {
    return translations.indexer_keyboardInstructionText();
  }
}
